import { ChangeDetectionStrategy, Component, computed, inject, input, output, Signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { boundsCurrencySelector } from '@fcom/common/store/selectors/bounds.selector';
import { isFinnairNorraOrWetLease, resolveTailNames } from '@fcom/core/utils';
import { cartDataSelector } from '@fcom/booking/store/selectors';
import { Category, FinnairBoundItem, FinnairItineraryItemFlight, FinnairPassengerCode } from '@fcom/dapi/api/models';
import { Amount } from '@fcom/dapi/interfaces';
import { IconButtonSize } from '@fcom/ui-components/components/buttons';

import { filterServices, isAncillaryBundle, SEAT_BUNDLE_CATEGORIES } from '../../modules/ancillaries/utils';
import { CartLineItem } from '../../utils/cart-line-items.utils';
import { getServicesForBoundWithUpsell } from '../../utils/common-booking.utils';
import { getSummaryForCategory, ServiceType } from '../../utils/services-summary.utils';
import {
  categoryStatusMapSelector,
  cityNameByLocationCodeSelector,
  localCartLineItemsSelector,
  nonSeatBundleConstituentsByFlightSelector,
  serviceCategoryOrderSelector,
  serviceCategoryTranslationsMapSelector,
} from '../../store';
import { BENEFITS } from '../../utils/benefit.utils';
import {
  filterServiceItemByPassenger,
  getPassengerAndInfantIds,
  multiplyLabelAmount,
} from '../../utils/shopping-cart.utils';
// A benefit row shown under the bound's fare family ("what's included in your ticket"), distinct from the
// purchasable product lines.
interface BenefitRow {
  title?: string;
  titleKey?: string;
  count?: number;
}

// One category's purchasable line items within this bound, plus the copy resolved for it, handed to
// ShoppingCartCategoryLineComponent to render as a product (or combo) line.
interface CartCategoryGroup {
  category: Category;
  items: CartLineItem[];
  categoryTitle?: string;
  comboConstituentTitles: string[];
}

/**
 * One bound (leg) of the shopping cart: its header, fare-family row, included benefits and the purchasable
 * product lines. Each bound resolves its own benefits and product groups from the store for its single
 * bound, so the parent cart simply decides which bounds to show and loops — no bound-keyed dictionaries.
 */
@Component({
  selector: 'fin-shopping-cart-bound',
  standalone: false,
  templateUrl: './shopping-cart-bound.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartBoundComponent {
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly IconButtonSize = IconButtonSize;

  private store$ = inject<Store<BookingAndAppState>>(Store);

  // The committed cart bound (leg) this component renders. City names and tail graphics are derived here from
  // the bound's own location and airline codes, so the parent just loops `cart.bounds` and hands each over.
  bound = input.required<FinnairBoundItem>();
  selectedPassengerId = input<string | null>(null);
  // The per-bound fare price and journey passenger count shown in the fare-family row (journey-level, so
  // passed in rather than re-derived per bound).
  price = input<Amount>();
  passengerCount = input<number>();

  openFareFamilyDetails = output<void>();
  openItineraryDetails = output<void>();

  private ignoredBenefits = ['rebook', 'cancel', 'plusPoints'];

  protected cart = this.store$.selectSignal(cartDataSelector);
  protected currency = this.store$.selectSignal(boundsCurrencySelector);
  // Location-code → city-name lookup (cart + order), so the header resolves city names from the bound's own
  // endpoint codes rather than relying on the parent to pre-enrich them.
  protected cityNameByLocationCode = this.store$.selectSignal(cityNameByLocationCodeSelector);
  private categoryStatus = this.store$.selectSignal(categoryStatusMapSelector);
  private localLineItems = this.store$.selectSignal(localCartLineItemsSelector);
  private categoryTranslations = this.store$.selectSignal(serviceCategoryTranslationsMapSelector);
  private bundleConstituents = this.store$.selectSignal(nonSeatBundleConstituentsByFlightSelector);
  // The catalog's category display order, used to order the product lines the same way Travel Extras does.
  private serviceCategoryOrder = this.store$.selectSignal(serviceCategoryOrderSelector);
  private includedServices = computed(() =>
    filterServices(this.cart()?.services?.unpaid, (service) => service.includedInTicketType)
  );

  // Tail-colour graphics for the operating airlines, derived from the bound's own operating-airline codes.
  protected tailNames = computed(() => resolveTailNames(this.bound().operatingAirlineCodes));

  private refundableBenefits: Signal<BenefitRow[]> = computed(() => {
    const refundable = this.cart()?.benefits[this.bound().fareFamily?.code]?.translatedBenefitRows?.find(
      (row) => row.key === BENEFITS.cancel
    );
    return refundable ? [{ title: refundable.label }] : [];
  });

  private changeableBenefits: Signal<BenefitRow[]> = computed(() => {
    const changeable = this.cart()?.benefits[this.bound().fareFamily?.code]?.translatedBenefitRows?.find(
      (row) => row.key === BENEFITS.rebook
    );
    return changeable ? [{ title: changeable.label }] : [];
  });

  private partnerBenefits: Signal<BenefitRow[]> = computed(() => {
    const cart = this.cart();
    if (!cart) {
      return [];
    }
    const bound = this.bound();
    const hasOnlyAdults = cart.passengers.every((pax) => pax.passengerTypeCode === FinnairPassengerCode.ADT);
    if (!hasOnlyAdults) {
      return [{ titleKey: 'shoppingCart.partnerAirlineAllowances' }];
    }
    const passengerCount = cart.passengers.length;
    return cart.benefits[bound.fareFamily.code].translatedBenefitRows
      .filter((benefit) => benefit.isPositive && !this.ignoredBenefits.includes(benefit.key))
      .flatMap((benefit) => (benefit.children ? benefit.children : benefit))
      .map((benefit) => ({ title: multiplyLabelAmount(benefit.label, passengerCount) }));
  });

  // The bound's included-service summary ("what's already in your ticket") for Finnair/Norra/wet-lease
  // operated bounds, resolved with upsell and filtered to the selected passenger.
  private includedServiceBenefits: Signal<BenefitRow[]> = computed(() => {
    const cart = this.cart();
    if (!cart) {
      return [];
    }
    const bound = this.bound();
    const serviceItems = getServicesForBoundWithUpsell(
      cart,
      false,
      this.includedServices(),
      cart.services.servicesOrder,
      bound,
      cart.serviceCatalog,
      this.categoryStatus()
    );
    const isShortHaul = bound.itinerary.every((item: FinnairItineraryItemFlight) => item.isShortHaul);
    const passengerId = this.selectedPassengerId();
    return serviceItems.reduce((rows, item) => {
      const filteredItem = filterServiceItemByPassenger(item, passengerId, cart.passengers);
      return getSummaryForCategory(item.category, bound, filteredItem, isShortHaul)
        .filter((summaryItem) => summaryItem.type !== ServiceType.NON_INCLUDED)
        .reduce((acc, { quantity, text, description }) => {
          acc.push({ title: text, titleKey: description, count: quantity });
          return acc;
        }, rows);
    }, [] as BenefitRow[]);
  });

  // Finnair/Norra/wet-lease bounds carry their own included-service summary; other operators fall back to a
  // generic partner-allowance row instead.
  private isOperatedByFinnairGroup = computed(() => {
    const flight = this.bound().itinerary[0] as FinnairItineraryItemFlight;
    return isFinnairNorraOrWetLease(flight.operatingAirline.name, flight.operatingAirline.code);
  });

  protected benefits: Signal<BenefitRow[]> = computed(() => [
    ...this.refundableBenefits(),
    ...this.changeableBenefits(),
    ...(this.isOperatedByFinnairGroup() ? this.includedServiceBenefits() : this.partnerBenefits()),
  ]);

  // The purchasable products for this bound: the local desired selection filtered to the selected passenger
  // and grouped by category, with benefits excluded and copy resolved. Driven purely off local state — no
  // `dirty` flag, no committed/pending split — so a combo and the standalones it displaces resolve for free.
  protected productGroups: Signal<CartCategoryGroup[]> = computed(() => {
    const bound = this.bound();
    const flightId = (bound.itinerary[0] as FinnairItineraryItemFlight).id;
    const passengerId = this.selectedPassengerId();
    const paxIds = passengerId ? new Set(getPassengerAndInfantIds(this.cart(), passengerId)) : null;
    const boundItems = this.localLineItems().filter(
      (item) =>
        !item.isBenefit &&
        (item.fragmentId === bound.id || item.fragmentId === flightId) &&
        (!paxIds || paxIds.has(item.paxId))
    );
    const categoryTranslations = this.categoryTranslations();
    const bundleConstituents = this.bundleConstituents();
    const categoryOrder = this.serviceCategoryOrder();
    return this.groupLineItemsByCategory(boundItems)
      .sort(
        (a, b) =>
          this.categoryRank(a.category, categoryOrder) - this.categoryRank(b.category, categoryOrder) ||
          a.category.localeCompare(b.category)
      )
      .map(({ category, items }) => ({
        category,
        items,
        categoryTitle: categoryTranslations?.[category]?.title,
        comboConstituentTitles: isAncillaryBundle(category)
          ? (bundleConstituents?.[category]?.[flightId] ?? []).map((service) => service.translations?.title ?? '')
          : [],
      }));
  });

  // Groups this bound's line items by category, dropping any category whose quantities cancel out to nothing.
  private groupLineItemsByCategory(items: CartLineItem[]): Array<{ category: Category; items: CartLineItem[] }> {
    const byCategory = new Map<Category, CartLineItem[]>();
    items.forEach((item) => {
      byCategory.set(item.topCategory, [...(byCategory.get(item.topCategory) ?? []), item]);
    });
    return Array.from(byCategory, ([category, categoryItems]) => ({ category, items: categoryItems })).filter(
      ({ items: categoryItems }) => categoryItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0) > 0
    );
  }

  // Sort key for a product line's category, mirroring Travel Extras: seat bundles first, in their own fixed
  // order (a bound can show several — e.g. different passengers picking different seat bundles — and that
  // order must not vary between bounds or renders), then the remaining categories in catalog order, with
  // anything absent from the catalog falling to the end. Seat bundles are pinned here rather than read from
  // the catalog because they are not part of it yet; once they are, this key keeps them first and single.
  private categoryRank(category: Category, catalogOrder: Category[]): number {
    const seatBundleIndex = SEAT_BUNDLE_CATEGORIES.indexOf(category);
    if (seatBundleIndex !== -1) {
      return seatBundleIndex;
    }
    const catalogIndex = catalogOrder.indexOf(category);
    return SEAT_BUNDLE_CATEGORIES.length + (catalogIndex === -1 ? catalogOrder.length : catalogIndex);
  }
}
